import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import {
  getIllustrationDetails,
  resetRegionalIllustrationToDefault,
  updateRegionalIllustration,
  checkVpnConnection,
  // getDarkroomStorageUrls,
  // uploadFileToBucket,
  // checkVpnConnection,
} from "../api/illustrationsApi";
import AssetSelector from "../../common/components/AssetSelector";
// import { getRasterImageFromSvg } from "../utils/illustrationTransformationUtils";
import styles from "./UpdateRegionalIllustrationPage.module.css";

function UpdateRegionalIllustrationPage() {
  const history = useHistory();

  // const canvasRef = useRef(null);

  // Context state
  const { user } = useContext(UserContext);

  // Input value states
  const [illustrationFile, setIllustrationFile] = useState(null);
  const [illustrationFileContent, setillustrationFileContent] = useState(null);
  const [resetToDefault, setResetToDefault] = useState(null);

  // Error states
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  // Illustration State
  const [illustrationDetailsObj, setIllustrationDetailsObj] = useState(null);
  const [regionalIllustrationDetailsObj, setRegionalIllustrationDetailsObj] =
    useState(null);

  // Page loading state
  const [isLoading, setIsLoading] = useState(true);

  // Params state
  const { defaultId, regionalId } = useParams();

  // Notification state
  const [notification, setNotification] = useState(null);

  const regionName = regionalIllustrationDetailsObj?.region_name;
  const vpnRequiredMessage = `Please connect to ${process.env.REACT_APP_VPN_ENVIRONMENT} VPN`;

  const ensureVpnConnection = async () => {
    const isUserConnectedToVpn = await checkVpnConnection();
    if (!isUserConnectedToVpn) {
      setIsLoading(false);
      setNotification({
        title: "VPN connection error",
        content: vpnRequiredMessage,
        isSuccess: false,
        isDanger: true,
      });
    }
    return isUserConnectedToVpn;
  };

  function redirectWithNotification(content) {
    history.push(`/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${defaultId}`, {
      notification: {
        title: "Illustration updated",
        content:
          content ||
          `Illustration for region ${regionName} has been updated successfully`,
        isSuccess: true,
        isDanger: false,
      },
    });
  }

  // function buildDarkroomSyncMessage(skippedOrFailed) {
  //   if (!skippedOrFailed) {
  //     return `Illustration for region ${regionName} has been updated successfully`;
  //   }
  //   const reason =
  //     skippedOrFailed === "VPN required"
  //       ? "skipped (VPN required)"
  //       : "unsuccessful";
  //   return `Illustration for region ${regionName} has been updated in Coffer. Darkroom/BCS sync was ${reason}; apps may not show the new asset until sync succeeds.`;
  // }

  // function buildBcsResetMessage(skippedOrFailed) {
  //   if (!skippedOrFailed) {
  //     return `Illustration for region ${regionName} has been reset to default successfully`;
  //   }
  //   const reason =
  //     skippedOrFailed === "VPN required"
  //       ? "skipped (VPN required)"
  //       : "unsuccessful";
  //   return `Illustration for region ${regionName} has been reset in Coffer. BCS reset was ${reason}; apps may still use the previous regional value until sync succeeds.`;
  // }

  // const uploadRegionalIllustrationToDarkroom = async (canvas, svgFile) => {
  //   const blob = await getRasterImageFromSvg(svgFile, canvas);
  //   const darkroomResponse = await getDarkroomStorageUrls(
  //     illustrationDetailsObj.combined_name
  //   );
  //   await uploadFileToBucket(
  //     darkroomResponse.data.data.upload_url,
  //     blob,
  //     user.idToken
  //   );
  //   return darkroomResponse;
  // };

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    if (illustrationFile || resetToDefault) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("A file must be selected");
      return;
    }

    if (illustrationFile) {
      setIsLoading(true);
      setNotification(null);
      try {
        const isUserConnectedToVpn = await ensureVpnConnection();
        if (!isUserConnectedToVpn) {
          return;
        }
        await updateRegionalIllustration(
          defaultId,
          regionalId,
          user.uid,
          illustrationDetailsObj.combined_name,
          regionalIllustrationDetailsObj.region_identifier,
          illustrationFile
        );

        setIsLoading(false);
        redirectWithNotification();
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.error(error.message);
        } else {
          console.error(error);
        }
        setNotification({
          title: "Failed to update Illustration",
          content: `An error occured while updating the Illustration : ${error.message}. Please refresh the page and try again.`,
          isSuccess: false,
          isDanger: true,
        });
      }
    }

    if (resetToDefault) {
      setIsLoading(true);
      setNotification(null);
      try {
        const isUserConnectedToVpn = await ensureVpnConnection();
        if (!isUserConnectedToVpn) {
          return;
        }
        await resetRegionalIllustrationToDefault(
          defaultId,
          regionalId,
          user.uid
        );

        setIsLoading(false);
        redirectWithNotification(
          `Illustration for region ${regionName} has been reset to default successfully`
        );
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.error(error.message);
        } else {
          console.error(error);
        }
        setNotification({
          title: "Failed to update Illustration",
          content: `An error occured while resetting the Illustration : ${error.message}. Please refresh the page and try again.`,
          isSuccess: false,
          isDanger: true,
        });
      }
    }
  };

  useEffect(() => {
    getIllustrationDetails(defaultId)
      .then((doc) => {
        setIllustrationDetailsObj(doc);
        setRegionalIllustrationDetailsObj(
          _.find(doc.regional_illustrations, ["id", regionalId])
        );
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [setIllustrationDetailsObj, setIsLoading, defaultId, regionalId]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <React.Fragment>
          <div className={`panel ${notification ? "show" : ""}`}>
            <div className={`animateFlag ${notification ? "visible" : ""}`}>
              <Flag
                success={notification ? notification.isSuccess : false}
                danger={notification ? notification.isDanger : false}
                floating
                title={notification ? notification.title : ""}
                content={notification ? notification.content : ""}
                onDismiss={() => {
                  setNotification(null);
                }}
              />
            </div>
          </div>
          <div className={styles.updateRegionalIllustrationPageContainer}>
            {/* <canvas style={{ display: "none" }} ref={canvasRef}></canvas> */}
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb>
                <Crumb text="assets" tagProps={{ to: "/" }} as={Link} />
                <Separator />
                <Crumb
                  text={illustrationDetailsObj.combined_name}
                  tagProps={{
                    to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${defaultId}`,
                  }}
                  as={Link}
                />
                <Separator />
                <Crumb
                  text="update regional illustration"
                  tagProps={{
                    to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}/${defaultId}/${regionalId}/${routes.UPDATE}`,
                  }}
                  as={Link}
                />
              </Breadcrumb>
            </div>
            <div>
              <h3 className={styles.heading3XL}>
                {`${illustrationDetailsObj.combined_name} (${regionalIllustrationDetailsObj.region_name})`}
              </h3>
              <h5 className={styles.headingXL}>Edit Regional Illustration</h5>
              <div className={styles.flagContainer}>
                <Flag
                  page
                  info
                  title={`${process.env.REACT_APP_VPN_ENVIRONMENT} VPN required`}
                  content={`You need to be connected to the ${process.env.REACT_APP_VPN_ENVIRONMENT} VPN to upload or update regional illustrations.`}
                />
              </div>
              <div className={styles.illustrationSelectorContainer}>
                <div className={styles.inputContainer}>
                  <AssetSelector
                    asset={
                      illustrationFileContent
                        ? illustrationFileContent
                        : regionalIllustrationDetailsObj.same_as_default
                        ? illustrationDetailsObj.default.img_url
                        : regionalIllustrationDetailsObj.img_url
                    }
                    onAssetSelected={(file, fileContent) => {
                      if (file) {
                        setIllustrationFile(file);
                        setillustrationFileContent(fileContent);
                        setAssetSelectorError(null);
                        setResetToDefault(false);
                        setRegionalIllustrationDetailsObj({
                          ...regionalIllustrationDetailsObj,
                          same_as_default: false,
                        });
                      }
                    }}
                    showResetToDefaultAction={true}
                    disableResetToDefaultAction={
                      regionalIllustrationDetailsObj.same_as_default
                    }
                    onResetToDefualtActionClick={() => {
                      setIllustrationFile(null);
                      setillustrationFileContent(null);
                      setRegionalIllustrationDetailsObj({
                        ...regionalIllustrationDetailsObj,
                        same_as_default: true,
                      });
                      setResetToDefault(true);
                    }}
                  />
                </div>
                {assetSelectorError ? (
                  <Flag title={assetSelectorError} />
                ) : null}
              </div>
              <Stack>
                <Button primary onClick={handleSaveClick}>
                  Save
                </Button>
                <Button secondary onClick={handeCancelClick}>
                  Cancel
                </Button>
              </Stack>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default UpdateRegionalIllustrationPage;
